import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, Checkbox, FormGroup, FormControlLabel } from "@mui/material";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  type Libraries,
} from "@react-google-maps/api";
import { getStatusMap } from "../../../../api";
import type {
  StatusMapSite,
  StatusMapSiteEntry,
} from "../../../../types/status-map";

const libraries: Libraries = ["geometry", "drawing"];

type MapProps = {
  selectId: (value: number) => void;
  selectOperator: (value: string) => void;
};

export default function Map({ selectId, selectOperator }: MapProps) {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const container = {
    width: "100%",
    height: "824px",
  };

  const [mapBounds, setMapBounds] = useState<google.maps.Map | null>(null);
  const [damageOnly, setDamageOnly] = useState(false);
  const [sites, setSites] = useState<StatusMapSiteEntry[] | undefined>();
  const [position, setPosition] = useState<google.maps.LatLngLiteral>({
    lat: 35.6813985,
    lng: 139.673278,
  });

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "",
    libraries,
  });

  const [previousSites, setPreviousSites] = useState<StatusMapSiteEntry[]>([]);

  const arraysEqual = (
    arr1: StatusMapSiteEntry[],
    arr2: StatusMapSiteEntry[]
  ) => JSON.stringify(arr1) === JSON.stringify(arr2);

  const handleBoundsChanged = async () => {
    if (!mapBounds || !disasterId) {
      return;
    }
    const bounds = mapBounds.getBounds();
    const center = mapBounds.getCenter();
    if (!bounds || !center) {
      return;
    }
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    try {
      const response = await getStatusMap({
        disaster_id: disasterId,
        ne_lat: ne.lat(),
        sw_lat: sw.lat(),
        ne_lng: ne.lng(),
        sw_lng: sw.lng(),
        damage_only: damageOnly,
        limit: null,
      });
      if (
        response.status === "ok" &&
        !arraysEqual(response.sites, previousSites)
      ) {
        setPosition({ lat: center.lat(), lng: center.lng() });
        setSites(response.sites);
        setPreviousSites(response.sites);
      }
    } catch (error) {
      console.error("GETリクエストエラー:", error);
    }
  };

  useEffect(() => {
    handleBoundsChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [damageOnly]);

  const markerClick = (site: StatusMapSite) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("site-id", String(site.id));
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.replaceState({ path: newUrl }, "", newUrl);
    selectId(site.id);
    selectOperator(`${site.operator}\n${site.name}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "0 40px 32px 40px",
        "@media screen and (max-width:900px)": {
          padding: "0 8px 8px 8px",
          height: "400px",
        },
      }}
    >
      <FormGroup>
        <FormControlLabel
          control={<Checkbox size="small" />}
          checked={damageOnly}
          onChange={(_event, checked) => setDamageOnly(checked)}
          label="被害ありSSのみ抽出"
          sx={{
            color: "error.main",
            fontSize: "12px",
            fontWeight: "bold",
            marginLeft: "0",
          }}
        />
      </FormGroup>
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={container}
          center={position}
          zoom={5.7}
          onLoad={(map) => setMapBounds(map)}
          onIdle={handleBoundsChanged}
        >
          {sites?.map((site, index) => (
            <MarkerF
              key={site.site.id}
              position={{ lat: site.site.lati, lng: site.site.longi }}
              options={{
                clickable: true,
                icon: {
                  scale: 0.002,
                  url: `https://ljs-dic-public-resources-dev.s3.ap-northeast-1.amazonaws.com/${site.icon_file_key}`,
                  scaledSize: new window.google.maps.Size(40, 40),
                } as google.maps.Icon,
              }}
              onClick={() => markerClick(site.site)}
            />
          ))}
        </GoogleMap>
      )}
    </Box>
  );
}
